import { useState, useEffect, useRef, useContext, useCallback } from "react";
import { useNavigate, Outlet, useLocation } from "react-router-dom";
import { Icon } from "@iconify/react";
import { useToast } from "../../design-system/contexts/ToastContext";
import { API, graphqlOperation } from "aws-amplify";
import { useLiveQuery } from "dexie-react-hooks";
import Dexie from "dexie";
import AuthContext from "../../contexts/AuthContext";
import { useTheme } from "../../design-system/contexts/ThemeContext";
import { Avatar } from "../../design-system";
import { BottomNavbar } from "../../components/BottomNavbar";
import MenuActionsModal from "../../components/MenuActionsModal/MenuActionsModal";
import ProfileModal from "../../components/ProfileModal";
import UnsyncDataModal from "../../components/UnsyncDataModal";
import ChromeWarningBanner from "../../components/ChromeWarningBanner";
import SecondaryNav from "../../components/SecondaryNav";
import DesktopHeader from "../../components/DesktopHeader";
// iOS keyboard is now handled via CSS dvh units and viewport meta tag
import {
  getSponsorLeads,
  downloadLeads,
  addSponsorLead,
} from "../../common/api";
import { leads, events } from "../../common/db";
import {
  sorter,
  updatingIndexedData,
  updateLeadApi,
  safeJSONParse,
  shouldShowTeamsFeature,
  changeDate,
} from "../../common/functions";
import {
  onCreateSponsorLead,
  onUpdateSponsorLead,
} from "../../graphql/subscriptions";
import "./ViewLeads.css";

// Inline SVG for cloud-off icon (works offline)
const CloudOffIcon = ({ width = 20, height = 20 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={width}
    height={height}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M9.58 5.548c.24 -.11 .492 -.207 .752 -.286c1.88 -.572 3.956 -.193 5.444 1c1.488 1.19 2.162 3.007 1.77 4.769h.99c1.913 0 3.464 1.56 3.464 3.486c0 .957 -.383 1.824 -1.003 2.454m-2.997 1.03h-11.343c-2.572 -.004 -4.657 -2.011 -4.657 -4.487c0 -2.475 2.085 -4.482 4.657 -4.482c.393 -.003 .786 .043 1.167 .136" />
    <path d="M3 3l18 18" />
  </svg>
);

const ViewLeads = () => {
  // ===== State =====
  const [filterData, setFilterData] = useState();
  const [refreshDisabled, setRefreshDisabled] = useState(false);
  const [downloadDisabled, setDownloadDisabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [networkStatus, setNetworkStatus] = useState(navigator.onLine);
  const [searchValue, setSearchValue] = useState("");
  const [eventName, setEventName] = useState("");
  const [eventDate, setEventDate] = useState("");
  // Detect iPad specifically (even in landscape/desktop mode)
  const isIPad = /iPad/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  // Detect if iPad is in landscape mode
  const [isLandscape, setIsLandscape] = useState(window.innerWidth > window.innerHeight);

  // isDesktop should be FALSE on iPad, even in landscape mode
  // iPad should use mobile UI (hamburger menu, bottom nav, etc.)
  const [isDesktop, setIsDesktop] = useState(() => {
    return window.innerWidth >= 992 && !isIPad;
  });

  // Update landscape detection on resize/orientation change
  useEffect(() => {
    const handleResize = () => {
      setIsLandscape(window.innerWidth > window.innerHeight);
      // iPad should always use mobile UI regardless of screen width
      if (isIPad) {
        setIsDesktop(false);
        return;
      }
      setIsDesktop(window.innerWidth >= 992);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, [isIPad]);
  const [menuModalOpen, setMenuModalOpen] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [unsyncModalOpen, setUnsyncModalOpen] = useState(false);
  const [unsyncLeadName, setUnsyncLeadName] = useState("");

  const searchRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const Context = useContext(AuthContext);
  const { theme, toggleTheme } = useTheme();
  const { success, error: showError } = useToast();

  // ===== URL Parsing =====
  const fetch_url_search = window.location.search;
  const urlParams = new URLSearchParams(fetch_url_search);
  const sponsor_code =
    urlParams.get("sponsor") || localStorage.getItem("sponsorCode");

  const fetch_url = window.location.href;
  // Handle both /events/ and /Events/ URL formats
  const eventsIndex = fetch_url.toLowerCase().indexOf("events/");
  const event_id = fetch_url.substring(
    eventsIndex + 7,
    fetch_url.lastIndexOf("/view-leads")
  );

  // Email regex for validation
  const validEmailRegex =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;

  // ===== IndexedDB Queries =====
  const allItems = useLiveQuery(
    async () => await leads.where("eventId").equals(event_id).toArray(),
    [event_id]
  );
  
  // Get event details to use the API's leadsCount (which is correct)
  const eventDetails = useLiveQuery(
    async () => {
      const eventData = await events.where("eventId").equals(event_id).toArray();
      return eventData[0] || null;
    }
  );
  
  // Helper to check if a lead is invalid (used for count calculation)
  const isInvalidLeadForCount = (lead) => {
    const name = lead.name?.trim() || "";
    const bookingId = lead.bookingId?.trim() || "";
    const emailId = lead.emailId?.trim() || "";
    const designation = lead.designation?.trim() || "";
    const organisation = lead.organisation?.trim() || "";
    const phoneNumber = lead.phoneNumber?.trim() || "";

    const nameMatchesBookingId = name.toLowerCase() === bookingId.toLowerCase();
    const hasRealAttendeeData =
      (designation && designation !== "null") ||
      (organisation && organisation !== "null") ||
      (phoneNumber && phoneNumber !== "null") ||
      (emailId && emailId !== "null" && emailId.toLowerCase() !== bookingId.toLowerCase());

    if (nameMatchesBookingId && !hasRealAttendeeData) {
      return true;
    }
    if (!name && !emailId && bookingId && !hasRealAttendeeData) {
      return true;
    }
    return false;
  };

  // Calculate count from IndexedDB leads - excludes invalid/unrecognized leads
  const calculatedCount = useLiveQuery(
    async () => {
      const allLeads = await leads.where("eventId").equals(event_id).toArray();
      // Filter out invalid leads - only count valid leads with real attendee data
      const validLeads = allLeads.filter(lead => {
        // Exclude invalid leads
        if (isInvalidLeadForCount(lead)) {
          return false;
        }

        const name = lead.name?.trim() || "";
        const bookingId = lead.bookingId?.trim() || "";

        // Count if name exists and is not "Unknown"
        if (name !== "" && name.toLowerCase() !== "unknown") {
          return true;
        }

        // For manual leads: if name is empty but bookingId exists and looks like an email, count it
        if (!name && bookingId && validEmailRegex.test(bookingId)) {
          return true;
        }

        // Also count if bookingId exists (even if not email) - manual entries might use bookingId as identifier
        if (!name && bookingId && bookingId.length > 0) {
          return true;
        }

        return false;
      });
      return validLeads.length;
    }
  );
  
  // Use calculated count (real-time) but sync with API count when available
  // The calculated count should match the API count after filtering
  const allAttendeeCount = calculatedCount ?? eventDetails?.leadsCount ?? 0;

  const localProfileName = localStorage.getItem("profileName");
  const profileName =
    localProfileName && localProfileName !== "undefined"
      ? safeJSONParse(localProfileName)
      : Context?.state?.user?.attributes?.name ||
        Context?.state?.user?.attributes?.email ||
        "reachus@konfhub.com";

  let data = [];
  let loop = false;
  let leadList = [];

  // Offline data
  const offlineData = allItems?.filter((singleDataSet) => {
    return singleDataSet.network === "false";
  });

  // Check if a lead is manually added (no registered name)
  const isManualLead = (lead) => {
    const name = lead.name?.trim();
    return !name || name.toLowerCase() === "unknown";
  };

  // Check if a lead is invalid/unrecognized (scanned but not a valid attendee for this event)
  // Invalid leads have no real attendee data - just the raw scanned value
  const isInvalidLead = (lead) => {
    // Manually saved leads should never be considered invalid
    // They are intentionally added by the user, even if they don't have all attendee data
    if (lead._manualSave === true) {
      return false;
    }

    const name = lead.name?.trim() || "";
    const bookingId = lead.bookingId?.trim() || "";
    const emailId = lead.emailId?.trim() || "";
    const designation = lead.designation?.trim() || "";
    const organisation = lead.organisation?.trim() || "";
    const phoneNumber = lead.phoneNumber?.trim() || "";

    // If name equals bookingId (case-insensitive), it's likely just raw scanned data
    const nameMatchesBookingId = name.toLowerCase() === bookingId.toLowerCase();

    // Check if there's any real attendee data
    const hasRealAttendeeData =
      (designation && designation !== "null") ||
      (organisation && organisation !== "null") ||
      (phoneNumber && phoneNumber !== "null") ||
      (emailId && emailId !== "null" && emailId.toLowerCase() !== bookingId.toLowerCase());

    // Invalid if: name matches bookingId AND no real attendee data
    // This means a QR was scanned but it didn't match any registered attendee
    if (nameMatchesBookingId && !hasRealAttendeeData) {
      return true;
    }

    // Also invalid if: no name, no email, and bookingId doesn't look like a valid konfhub booking
    // (valid bookingIds are typically alphanumeric codes, not random strings)
    if (!name && !emailId && bookingId && !hasRealAttendeeData) {
      return true;
    }

    return false;
  };

  // Helper function to get display name for a lead
  // Uses name if available, otherwise falls back to emailId or bookingId
  const getLeadDisplayName = (lead) => {
    const name = lead.name?.trim();

    // For invalid leads, just show the raw bookingId/scanned value
    if (isInvalidLead(lead)) {
      return lead.bookingId || lead.name || "Unknown";
    }

    if (name && name.toLowerCase() !== "unknown") {
      return name;
    }
    // Fallback to emailId or bookingId for manually added/non-registered leads
    const identifier = lead.emailId?.toLowerCase() || lead.bookingId || "Unknown";
    return `${identifier} (Manual)`;
  };

  // Filter leads - now includes all leads (no filtering by name)
  const filterValidLeads = (leadsArray) => {
    if (!leadsArray) return leadsArray;
    return leadsArray;
  };

  // ===== Effects =====
  // Note: isDesktop resize handler is already defined above with iPad detection

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Preload offline banner icons when component mounts (for offline support)
  useEffect(() => {
    // Preload icons by rendering them off-screen when online
    if (navigator.onLine) {
      const preloadContainer = document.createElement('div');
      preloadContainer.style.position = 'absolute';
      preloadContainer.style.left = '-9999px';
      preloadContainer.style.opacity = '0';
      preloadContainer.style.pointerEvents = 'none';
      document.body.appendChild(preloadContainer);
      
      // Create temporary icons to force load
      const wifiIcon = document.createElement('div');
      wifiIcon.innerHTML = '<iconify-icon icon="bx:wifi-off" width="30" height="30" style="color: #FFFFFF"></iconify-icon>';
      preloadContainer.appendChild(wifiIcon);
      
      const closeIcon = document.createElement('div');
      closeIcon.innerHTML = '<iconify-icon icon="mdi:close" width="18" height="18" style="color: #FFFFFF"></iconify-icon>';
      preloadContainer.appendChild(closeIcon);
      
      // Clean up after a delay
      setTimeout(() => {
        if (preloadContainer.parentNode) {
          document.body.removeChild(preloadContainer);
        }
      }, 5000);
    }
  }, []);

  useEffect(() => {
    const handleOnline = () => setNetworkStatus(true);
    const handleOffline = () => setNetworkStatus(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    fetchEventDetails();

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    if (sponsor_code) {
      localStorage.setItem("sponsorCode", sponsor_code);
    }
  }, [sponsor_code]);

  useEffect(() => {
    if (networkStatus) {
      if (offlineData) {
        syncDetailsAPI();
      } else {
        getSponsorLeadsApi();
      }
      setRefreshDisabled(false);
    } else {
      offlineFunction();
    }
  }, [networkStatus]);

  // ===== Subscriptions =====
  useEffect(() => {
    const createSubscription = API.graphql(
      graphqlOperation(onCreateSponsorLead)
    ).subscribe({
      next: async (updateData) => {
        let subscriptionRes = JSON.parse(
          updateData.value.data.onCreateSponsorLead
        );
        if (subscriptionRes.sponsorCode == sponsor_code) {
          let stringifyObjArr = subscriptionRes["comment"];
          if (
            subscriptionRes["comment"] &&
            subscriptionRes["comment"] !== "undefined"
          ) {
            stringifyObjArr = subscriptionRes["comment"].map((obj) =>
              JSON.stringify(obj)
            );
          }

          let fetchEventDetailsFromDBDET = await events
            .where("eventId")
            .equals(event_id)
            .toArray();

          let fetchAttendeeDetailsFromDB = [];
          if (fetchEventDetailsFromDBDET.city) {
            fetchAttendeeDetailsFromDB = await leads
              .where("detCode")
              .equals(subscriptionRes.detCode)
              .toArray();
          } else {
            // Get ALL leads for this event and match by any possible combination
            // This is more reliable than individual queries
            const allEventLeads = await leads.where("eventId").equals(event_id).toArray();
            const normalizedBookingId = subscriptionRes.bookingId?.toLowerCase();
            const normalizedEmailId = subscriptionRes.emailId?.toLowerCase();
            
            // Try to find by multiple criteria - check all possible matches
            fetchAttendeeDetailsFromDB = allEventLeads.filter(lead => {
              const leadBookingId = lead.bookingId?.toLowerCase();
              const leadId = lead.id?.toLowerCase();
              const leadEmailId = lead.emailId?.toLowerCase();
              
              // Match by bookingId (in id or bookingId field)
              if (normalizedBookingId && (
                leadId === normalizedBookingId || 
                leadBookingId === normalizedBookingId
              )) {
                return true;
              }
              
              // Match by emailId
              if (normalizedEmailId && (
                leadEmailId === normalizedEmailId ||
                leadId === normalizedEmailId ||
                leadBookingId === normalizedEmailId
              )) {
                return true;
              }
              
              return false;
            });
          }

          if (
            (subscriptionRes.bookingId || subscriptionRes.emailId) &&
            fetchAttendeeDetailsFromDB.length === 0
          ) {
            // Only create if name is valid, or use emailId/bookingId as fallback name
            // For manual leads, name might be empty but bookingId contains the email
            const subscriptionName = subscriptionRes.name?.trim();
            const isValidName = subscriptionName && subscriptionName !== "" && subscriptionName.toLowerCase() !== "unknown";
            // If name is empty but bookingId is an email, use bookingId as name
            let leadName = isValidName ? subscriptionName : "";
            if (!leadName && subscriptionRes.bookingId && validEmailRegex.test(subscriptionRes.bookingId)) {
              leadName = subscriptionRes.bookingId;
            }
            // Final fallback
            if (!leadName) {
              leadName = subscriptionRes.emailId || subscriptionRes.bookingId || "";
            }
            
            let creatingLeadData = {
              id: subscriptionRes.bookingId
                ? subscriptionRes.bookingId.toLowerCase()
                : subscriptionRes.emailId
                ? subscriptionRes.emailId
                : subscriptionRes,
              name: leadName,
              emailId: subscriptionRes.emailId ? subscriptionRes.emailId : "",
              phoneNumber: subscriptionRes.phoneNumber
                ? subscriptionRes.phoneNumber
                : "",
              bookingId: subscriptionRes.bookingId
                ? subscriptionRes.bookingId.toLowerCase()
                : subscriptionRes.emailId
                ? subscriptionRes.emailId
                : subscriptionRes,
              designation: subscriptionRes.designation
                ? subscriptionRes.designation
                : "",
              organisation: subscriptionRes.organisation
                ? subscriptionRes.organisation
                : "",
              createdAt: subscriptionRes.createdAt,
              eventId: subscriptionRes.eventId,
              sponsorCode: subscriptionRes.sponsorCode,
              network: "",
              createNetwork: "",
              comment: stringifyObjArr,
              scannedBy: subscriptionRes.scannedBy,
              status: subscriptionRes.status,
              detCode: subscriptionRes.detCode ?? "",
            };
            if (searchRef.current?.value) {
              addLeadtoIndexDB(creatingLeadData);
            } else {
              createLead(creatingLeadData);
            }
          } else if (fetchAttendeeDetailsFromDB && fetchAttendeeDetailsFromDB.length > 0) {
            // Update existing lead, including name if it differs from server
            const existingLead = fetchAttendeeDetailsFromDB[0];
            const newName = subscriptionRes.name?.trim();
            const isValidNewName = newName && newName !== "" && newName.toLowerCase() !== "unknown";
            const currentNameIsInvalid = existingLead?.name === existingLead?.bookingId ||
                                       existingLead?.name === existingLead?.id ||
                                       (existingLead?.emailId && existingLead?.name === existingLead?.emailId) ||
                                       !existingLead?.name?.trim() ||
                                       existingLead?.name?.trim().toLowerCase() === "unknown";
            const shouldUpdateName = isValidNewName && 
              (currentNameIsInvalid || existingLead?.name !== newName);

            updatingIndexedData({
              key: subscriptionRes.bookingId,
              network: true,
              comment: stringifyObjArr,
              ...(shouldUpdateName && { name: subscriptionRes.name }),
              emailId: subscriptionRes.emailId ? subscriptionRes.emailId : "",
              phoneNumber: subscriptionRes.phoneNumber
                ? subscriptionRes.phoneNumber
                : "",
              designation: subscriptionRes.designation
                ? subscriptionRes.designation
                : "",
              organisation: subscriptionRes.organisation
                ? subscriptionRes.organisation
                : "",
              createdAt: subscriptionRes.createdAt,
              scannedBy: subscriptionRes.scannedBy,
              detCode: subscriptionRes.detCode ?? "",
            });
          }
        }
      },
      error: (error) => console.warn(error),
    });

    const editSubscription = API.graphql(
      graphqlOperation(onUpdateSponsorLead)
    ).subscribe({
      next: async (updateData) => {
        let subscriptionRes = JSON.parse(
          updateData.value.data.onUpdateSponsorLeads
        );
        if (subscriptionRes.statusCode == 200) {
          let leadDataArr = subscriptionRes.body.data;
          if (leadDataArr.sponsorCode == sponsor_code) {
            leadDataArr.forEach((singleLead) => {
              let stringifyObjArr = singleLead["comment"];
              if (
                singleLead["comment"] &&
                singleLead["comment"] !== "undefined"
              ) {
                stringifyObjArr = singleLead["comment"].map((obj) =>
                  JSON.stringify(obj)
                );
              }
              if (searchRef.current?.value) {
                updatingIndexedData({
                  key: singleLead.bookingId,
                  network: true,
                  comment: stringifyObjArr,
                });
              } else {
                editLeadIndexedDB({
                  key: singleLead.bookingId,
                  network: true,
                  comment: stringifyObjArr,
                });
              }
            });
          }
        }
      },
      error: (error) => console.warn(error),
    });

    return () => {
      createSubscription.unsubscribe();
      editSubscription.unsubscribe();
    };
  }, []);

  // ===== API Functions =====
  const offlineFunction = async () => {
    let offlineDataSet = await leads
      .where("eventId")
      .equals(event_id)
      .toArray();
    setFilterData(filterValidLeads(offlineDataSet));
    setRefreshDisabled(true);
  };

  const fetchEventDetails = async () => {
    let fetchEventDetailsFromDB = await events
      .where("eventId")
      .equals(event_id)
      .toArray();
    setEventName(fetchEventDetailsFromDB[0]?.eventName || "");
    setEventDate(fetchEventDetailsFromDB[0]?.startDate || "");
  };

  const getSponsorLeadsApi = async (token) => {
    let sponsorCode = sponsor_code || localStorage.getItem("sponsorCode");
    try {
      const res = await getSponsorLeads(sponsorCode, token);

      // Check for GraphQL errors in response
      if (res.errors && res.errors.length > 0) {
        console.error("GraphQL errors in getSponsorLeads:", res.errors);
        const errorMessage = res.errors[0]?.message || "Failed to fetch leads";
        showError(errorMessage);
        setIsRefreshing(false);
        setRefreshDisabled(false);
        return;
      }

      let allAttendeeDetails = await res.data.getSponsorLeads;
      let parsedData = JSON.parse(allAttendeeDetails);
      let nextToken = parsedData.nextToken;
      let leadsData = parsedData.items;
      data = [...data, ...leadsData];

      if (leadsData.length === 0) {
        setDownloadDisabled(true);
      } else {
        setDownloadDisabled(false);
      }

      if (nextToken !== null) {
        getSponsorLeadsApi(nextToken);
        loop = true;
      } else {
        if (loop) {
          postAPIFunctions(data);
        } else {
          postAPIFunctions(leadsData);
        }
      }
    } catch (err) {
      console.error("Error in getSponsorLeads:", err);
      const errorMessage = err?.errors?.[0]?.message || err?.message || "Failed to fetch leads";
      showError(errorMessage);
      setIsRefreshing(false);
      setRefreshDisabled(false);
    }
  };

  const postAPIFunctions = async (allLeadDetails) => {
    // Reset leadList for fresh data
    leadList = [];
    
    // Get existing manually saved leads before clearing to preserve their flags
    const existingLeads = await leads.toArray();
    const manuallySavedLeads = new Map();
    existingLeads.forEach(lead => {
      if (lead._manualSave === true) {
        manuallySavedLeads.set(lead.id, {
          _manualSave: lead._manualSave,
          _savedAt: lead._savedAt
        });
      }
    });
    
    leads.clear();
    
    // Ensure we have valid data
    if (!allLeadDetails || allLeadDetails.length === 0) {
      setFilterData([]);
      setIsRefreshing(false);
      setDownloadDisabled(true);
      return;
    }
    
    let sortedAttendeesList = allLeadDetails.sort(sorter);

    sortedAttendeesList.forEach((entry) => {
      let stringifyObjArr = entry["comment"];
      if (entry["comment"] && entry["comment"] !== "undefined") {
        stringifyObjArr = entry["comment"].map((obj) => JSON.stringify(obj));
      }

      const leadId = entry["bookingId"];
      const manualSaveData = manuallySavedLeads.get(leadId);

      leadList.push({
        id: leadId,
        name: entry["name"],
        emailId: entry["emailId"],
        phoneNumber: entry["phoneNumber"],
        bookingId: entry["bookingId"],
        designation: entry["designation"],
        organisation: entry["organisation"],
        createdAt: entry["createdAt"],
        status: entry["status"],
        eventId: entry["eventId"],
        sponsorCode: entry["sponsorCode"],
        network: "",
        createNetwork: "",
        comment: stringifyObjArr,
        scannedBy: entry["scannedBy"],
        // Preserve _manualSave flag if this lead was manually saved
        ...(manualSaveData && { 
          _manualSave: manualSaveData._manualSave,
          _savedAt: manualSaveData._savedAt
        }),
      });
    });

    leads
      .bulkAdd(leadList)
      .then(() => {
        console.log("Done adding all leads");
        // Set the full data after IndexedDB is updated
        // Use setTimeout to ensure IndexedDB update is complete
        setTimeout(() => {
          // If there's a search value, re-apply the filter
          if (searchValue && searchValue.trim()) {
            const searchLower = searchValue.toLowerCase();
            const filtered = sortedAttendeesList.filter((dataItems) => {
              return (
                dataItems?.name?.toLowerCase().includes(searchLower) ||
                dataItems?.emailId?.toLowerCase().includes(searchLower) ||
                dataItems?.phoneNumber?.toLowerCase().includes(searchLower) ||
                dataItems?.bookingId?.toLowerCase().includes(searchLower)
              );
            });
            setFilterData(filterValidLeads(filtered));
          } else {
            setFilterData(filterValidLeads(sortedAttendeesList));
          }
          setIsRefreshing(false);
        }, 100);
      })
      .catch(Dexie.BulkError, (e) => {
        console.error("Some leads did not succeed");
        // Still set filter data even if some leads failed
        // If there's a search value, re-apply the filter
        if (searchValue && searchValue.trim()) {
          const searchLower = searchValue.toLowerCase();
          const filtered = sortedAttendeesList.filter((dataItems) => {
            return (
              dataItems?.name?.toLowerCase().includes(searchLower) ||
              dataItems?.emailId?.toLowerCase().includes(searchLower) ||
              dataItems?.phoneNumber?.toLowerCase().includes(searchLower) ||
              dataItems?.bookingId?.toLowerCase().includes(searchLower)
            );
          });
          setFilterData(filterValidLeads(filtered));
        } else {
          setFilterData(filterValidLeads(sortedAttendeesList));
        }
        setIsRefreshing(false);
      });
  };

  const formattingOfflineComments = (singleDatasetComment) => {
    let parsedComments = singleDatasetComment.map((item) => JSON.parse(item));
    let offlineComments = parsedComments.filter(
      (comment) => comment.isOnline === false
    );
    return offlineComments;
  };

  const formattingOnlineComments = (singleDatasetComment) => {
    let parsedComments = singleDatasetComment.map((item) => JSON.parse(item));
    let onlineComments = parsedComments.filter(
      (comment) => comment.isOnline === true
    );
    return onlineComments;
  };

  const syncDetailsAPI = () => {
    let arrOfLeadDetails = [];
    let createLeadDetailsArr = [];

    offlineData?.forEach((singleDataset) => {
      let emptyStringforComments = [];
      let isOnlineComments = [];
      let isPreviousOnlineComments = [];
      let indexedDBComments = [];
      var leadDetials = {};
      var createLeadDetials = {};

      if (singleDataset.createNetwork === "false") {
        if (!singleDataset.comment || singleDataset.comment === "undefined") {
          createLeadDetials = {
            bookingId: singleDataset.bookingId,
            eventId: event_id,
            sponsorCode: sponsor_code,
            comment: "",
            name: singleDataset.name,
            designation: singleDataset.designation
              ? singleDataset.designation
              : "",
            emailId: singleDataset.emailId ? singleDataset.emailId : "",
            phoneNumber: singleDataset.phoneNumber ? singleDataset.phoneNumber : "",
            organisation: singleDataset.organisation
              ? singleDataset.organisation
              : "",
            scannedBy: profileName,
            status: singleDataset["status"],
          };
          createLeadDetailsArr.push(createLeadDetials);

          editLeadIndexedDB({
            key: singleDataset.bookingId,
            network: true,
            comment: singleDataset.comment,
            createNetwork: true,
          });
        }
      }

      if (singleDataset.comment && singleDataset.comment !== "undefined") {
        let arrOfOfflineComments = formattingOfflineComments(
          singleDataset.comment
        );
        if (arrOfOfflineComments.length > 0) {
          arrOfOfflineComments.forEach((singleComment) => {
            let obj = { ...singleComment, isOnline: true };
            emptyStringforComments.push(JSON.stringify(obj));
          });

          if (singleDataset.createNetwork === "false") {
            createLeadDetials = {
              bookingId: singleDataset.bookingId,
              eventId: event_id,
              sponsorCode: sponsor_code,
              comment: emptyStringforComments,
              name: singleDataset.name,
              designation: singleDataset.designation
                ? singleDataset.designation
                : "",
              emailId: singleDataset.emailId ? singleDataset.emailId : "",
              phoneNumber: singleDataset.phoneNumber ? singleDataset.phoneNumber : "",
              organisation: singleDataset.organisation
                ? singleDataset.organisation
                : "",
              scannedBy: profileName,
              status: singleDataset["status"],
            };
            createLeadDetailsArr.push(createLeadDetials);
          } else {
            leadDetials = {
              bookingId: singleDataset.bookingId,
              eventId: event_id,
              sponsorCode: sponsor_code,
              comment: emptyStringforComments,
            };
            arrOfLeadDetails.push(leadDetials);
          }

          let arrOfOnlineComments = formattingOnlineComments(
            singleDataset.comment
          );

          if (arrOfOnlineComments && arrOfOfflineComments) {
            arrOfOnlineComments.forEach((item) => {
              isPreviousOnlineComments.push(JSON.stringify(item));
            });
            arrOfOfflineComments.forEach((singleComment) => {
              let obj = { ...singleComment, isOnline: true };
              isOnlineComments.push(JSON.stringify(obj));
            });
            indexedDBComments = [
              ...isPreviousOnlineComments,
              ...isOnlineComments,
            ];
          } else if (arrOfOnlineComments) {
            indexedDBComments = [...isPreviousOnlineComments];
          }
          editLeadIndexedDB({
            key: singleDataset.bookingId,
            network: true,
            comment: indexedDBComments,
          });
        }
      }
    });

    if (offlineData) {
      if (createLeadDetailsArr.length !== 0) {
        createLeadApi(createLeadDetailsArr);
        createLeadDetailsArr = [];
      }
      if (arrOfLeadDetails.length !== 0) {
        try {
          updateLeadApi(arrOfLeadDetails);
          arrOfLeadDetails = [];
        } catch (err) {
          console.log(err);
        }
      }
    }
  };

  const addLeadtoIndexDB = async (leadData) => {
    // Use put instead of add to handle duplicates gracefully
    try {
      await leads.put(leadData);
    } catch (err) {
      // If put fails, try to update instead
      try {
        await leads.update(leadData.id, leadData);
      } catch (updateErr) {
        console.error("Error saving lead to IndexedDB:", updateErr);
      }
    }
  };

  const createLead = async (leadData) => {
    addLeadtoIndexDB(leadData);
    var attendeesList = await leads.toArray();

    if (searchValue) {
      let dataFiltered = attendeesList?.filter((dataItems) => {
        return (
          dataItems?.name?.toLowerCase().includes(searchValue.toLowerCase()) ||
          dataItems?.emailId
            ?.toLowerCase()
            ?.includes(searchValue.toLowerCase()) ||
          dataItems?.phoneNumber
            ?.toLowerCase()
            ?.includes(searchValue.toLowerCase())
        );
      });
      setFilterData(filterValidLeads(dataFiltered));
    } else {
      setFilterData(filterValidLeads(attendeesList));
    }
  };

  const createLeadApi = async (leadData) => {
    try {
      const res = await addSponsorLead(leadData);

      // Check for GraphQL errors in response
      if (res.errors && res.errors.length > 0) {
        console.error("GraphQL errors in createLeadApi:", res.errors);
        const errorMessage = res.errors[0]?.message || "Failed to sync lead";
        showError(errorMessage);
      }
    } catch (err) {
      console.error("Error in createLeadApi:", err);
      // Show user-facing error message for GraphQL errors
      const errorMessage = err?.errors?.[0]?.message || err?.message || "Failed to sync lead";
      showError(errorMessage);
    }
  };

  const editLeadIndexedDB = async (data) => {
    try {
      updatingIndexedData(data);
      var attendeesList = await leads.toArray();
      if (searchValue) {
        let dataFiltered = attendeesList?.filter((dataItems) => {
          return (
            dataItems?.name
              ?.toLowerCase()
              .includes(searchValue.toLowerCase()) ||
            dataItems?.emailId
              ?.toLowerCase()
              ?.includes(searchValue.toLowerCase()) ||
            dataItems?.phoneNumber
              ?.toLowerCase()
              ?.includes(searchValue.toLowerCase())
          );
        });
        setFilterData(filterValidLeads(dataFiltered));
      } else {
        setFilterData(filterValidLeads(attendeesList));
      }
    } catch (err) {
      console.log(err);
    }
  };

  // ===== Event Handlers =====
  const handleLeadClick = async (lead) => {
    navigate({
      pathname: `/events/${event_id}/view-leads/DetailsCardModal`,
      search: `?bookingId=${lead.bookingId}`,
    });
  };

  // Debounced filter function to prevent cursor jumping on Samsung/Chrome
  const performFilter = useCallback((value) => {
    if (value) {
      const searchLower = value.toLowerCase();
      let dataFiltered = allItems?.filter((dataItems) => {
        // Search by name, email, phone, or bookingId
        return (
          dataItems?.name?.toLowerCase().includes(searchLower) ||
          dataItems?.emailId?.toLowerCase()?.includes(searchLower) ||
          dataItems?.phoneNumber?.toLowerCase()?.includes(searchLower) ||
          dataItems?.bookingId?.toLowerCase()?.includes(searchLower)
        );
      });
      setFilterData(filterValidLeads(dataFiltered));
    } else {
      setFilterData(filterValidLeads(allItems));
    }
  }, [allItems]);

  const filteringData = (value) => {
    // Update search value immediately for responsive UI
    setSearchValue(value);

    // Don't filter if we're currently refreshing to avoid state conflicts
    if (isRefreshing) {
      return;
    }

    // Clear any pending debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Debounce the actual filtering to prevent cursor jumping
    debounceTimerRef.current = setTimeout(() => {
      performFilter(value);
    }, 150);
  };

  const handleDownload = async () => {
    setLoading(true);
    try {
      const res = await downloadLeads(sponsor_code, event_id);

      // Check for GraphQL errors in response
      if (res.errors && res.errors.length > 0) {
        console.error("GraphQL errors in downloadLeads:", res.errors);
        const errorMessage = res.errors[0]?.message || "Failed to download leads";
        showError(errorMessage);
        setLoading(false);
        return;
      }

      let allLeads = JSON.parse(res.data.downloadSponsorLeads);
      if (allLeads.statusCode === 200) {
        setLoading(false);
        success("Downloading file");
        window.location.assign(allLeads.body.downloadUrl);
      } else {
        setLoading(false);
        const errorMsg = allLeads.body || "Download failed";
        showError(errorMsg);
      }
    } catch (err) {
      console.error("Error in downloadLeads:", err);
      const errorMessage = err?.errors?.[0]?.message || err?.message || "Failed to download leads";
      showError(errorMessage);
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    if (refreshDisabled || !networkStatus || isRefreshing) return;

    setIsRefreshing(true);

    // Clear any pending debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Preserve search value when refreshing - don't clear it
    // The search will be re-applied after data is loaded via the filteringData function

    // Reset data and loop variables for fresh fetch
    data = [];
    loop = false;
    leadList = [];
    
    // Ensure we reset refreshing state if API call fails immediately
    getSponsorLeadsApi().catch((err) => {
      console.error("Error in handleRefresh:", err);
      setIsRefreshing(false);
      setRefreshDisabled(false);
    });
  };

  // ===== Navigation Handlers =====
  const onClickHome = () => navigate({ pathname: "/events" });
  const onClickScan = () =>
    navigate({
      pathname: `/events/${event_id}/scanDetails`,
      search: `?sponsor=${sponsor_code}`,
    });
  const onClickTeam = () =>
    navigate({
      pathname: `/events/${event_id}/team`,
      search: `?sponsor=${sponsor_code}`,
    });
  const onClickAddEvent = () => navigate({ pathname: "/addEventModal" });
  const shareClickHandler = () =>
    navigate({ pathname: `/events/${event_id}/view-leads/ShareSponsorCode` });

  // ===== Format Date =====
  const formatEventDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // Use the reusable changeDate function from common/functions
  const formatCapturedAt = (dateStr) => {
    return changeDate(dateStr);
  };

  // Get subtitle for lead card
  const getLeadSubtitle = (lead) => {
    // Invalid leads should not show any subtitle
    if (isInvalidLead(lead)) {
      return "";
    }
    if (lead.designation && lead.organisation) {
      return `${lead.designation}, ${lead.organisation}`;
    }
    return lead.designation || lead.organisation || lead.emailId || "";
  };

  const isRouteModalOpen =
    location.pathname.includes("/DetailsCardModal") ||
    location.pathname.includes("/EditCommentModal") ||
    location.pathname.includes("/ShareSponsorCode");

  // ===== Render =====
  return (
    <div
      className={`view-leads-page ${refreshDisabled ? 'view-leads-page--offline' : ''} ${isRouteModalOpen ? 'view-leads-page--route-modal-open' : ''}`}
    >
      {/* Chrome Warning Banner (Desktop Only) */}
      <ChromeWarningBanner />

      {/* Mobile Header - Show on mobile or iPad (even in desktop/landscape mode) */}
      <header className={`view-leads-header ${isIPad ? 'view-leads-header--ipad' : ''} ${isIPad && isLandscape ? 'view-leads-header--ipad-landscape' : ''}`}>
        <div className="view-leads-header__left">
          <button className="view-leads-header__menu-btn" onClick={() => setMenuModalOpen(true)}>
            <Icon icon="solar:hamburger-menu-broken" width="24" height="24" />
          </button>
          <h1 className="view-leads-header__event-name">{eventName || "Event"}</h1>
        </div>
        <div className="view-leads-header__right">
          <button className="view-leads-header__theme-btn" onClick={toggleTheme}>
            <Icon
              icon={theme === "dark" ? "mdi:weather-sunny" : "mdi:weather-night"}
              width="24"
              height="24"
              color="currentColor"
              style={{ display: 'block' }}
            />
          </button>
          <Avatar
            src={Context?.state?.user?.attributes?.picture}
            name={Context?.state?.user?.attributes?.name}
            size={36}
            onClick={() => setProfileModalOpen(true)}
            className="view-leads-header__avatar"
          />
        </div>
      </header>

      {/* Offline Banner */}
      {refreshDisabled && (
        <div className="view-leads-offline-banner">
          <div className="view-leads-offline-banner__content">
            <div className="view-leads-offline-banner__icon-wrapper">
              <svg width="24" height="24" viewBox="0 0 20 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M0 5.52086L1.27933 6.80019C1.73533 6.34419 2.23114 5.94067 2.74686 5.57062L1.45395 4.27771C0.947286 4.65952 0.455095 5.06486 0 5.52086ZM4.68757 2.50348L2.1841 0L0.904762 1.27933L17.1905 17.5651L18.4698 16.2857L13.9351 11.751L14.8182 10.8689C13.805 9.86361 12.5128 9.18644 11.1096 8.92548L9.04852 6.86443C11.5719 6.69162 14.1595 7.54119 16.0414 9.42219L17.3208 8.14286C14.6988 5.51995 10.9322 4.58171 7.46971 5.28562L6.08995 3.90586C7.25436 3.54147 8.4672 3.3554 9.68729 3.35395C12.8874 3.35395 15.8731 4.5781 18.0952 6.80019L19.3746 5.52086C16.8114 2.95676 13.3706 1.54443 9.68729 1.54443C7.97555 1.55013 6.27992 1.87538 4.68757 2.50348ZM2.05381 8.14286L3.33314 9.42219C3.88765 8.87256 4.514 8.40051 5.19514 8.01891L3.8751 6.69886C3.22638 7.1069 2.61295 7.58462 2.05381 8.14286ZM4.55638 10.8698L5.83571 12.1491C6.46181 11.523 7.22452 11.1078 8.03338 10.8571L6.62467 9.44843C5.85547 9.79416 5.15485 10.2756 4.55638 10.8698ZM11.4869 14.3106L9.58686 12.4106C9.23842 12.4293 8.90285 12.5485 8.62074 12.7539C8.33863 12.9592 8.12205 13.2419 7.9972 13.5678C7.87234 13.8936 7.84456 14.2486 7.91721 14.5899C7.98987 14.9312 8.15984 15.2442 8.40657 15.4909C8.65331 15.7376 8.96625 15.9076 9.30754 15.9803C9.64883 16.0529 10.0039 16.0251 10.3297 15.9003C10.6555 15.7754 10.9382 15.5589 11.1436 15.2767C11.3489 14.9946 11.4682 14.6591 11.4869 14.3106Z" fill="white" stroke="white" strokeWidth="0.3"/>
              </svg>
            </div>
            <div className="view-leads-offline-banner__text">
              <span className="view-leads-offline-banner__title">You are offline.</span>
              <span className="view-leads-offline-banner__subtitle">All your leads will be synced once you are back online.</span>
            </div>
          </div>
          <button className="view-leads-offline-banner__close" onClick={() => setRefreshDisabled(false)}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      )}

      {/* Desktop Header */}
      <DesktopHeader
        eventName={eventName}
        eventDate={eventDate}
        onShareClick={shareClickHandler}
        onHomeClick={onClickHome}
        onAddEventClick={onClickAddEvent}
        onProfileClick={() => setProfileModalOpen(true)}
        isDesktop={isDesktop}
      />

      {/* Desktop Secondary Nav */}
      <SecondaryNav
        activeTab="leads"
        onScanClick={onClickScan}
        onLeadsClick={() => {}}
        onTeamClick={onClickTeam}
        isDesktop={isDesktop}
        showTeam={shouldShowTeamsFeature(eventDetails?.createdAt)}
      />

      {/* Content Container */}
      <div className="view-leads-card">
        <div className="view-leads-content">
        {/* Sticky Header (Count + Toolbar) */}
        <div className="view-leads-sticky-header">
          {/* Leads Count */}
          <div className="view-leads-count">
            <span className="view-leads-count__label">Leads Captured</span>
            <span className="view-leads-count__badge">{allAttendeeCount || 0}</span>
            <button
              className={`view-leads-count__refresh ${isRefreshing ? "view-leads-count__refresh--spinning" : ""}`}
              onClick={handleRefresh}
              disabled={refreshDisabled}
            >
              <Icon icon="mynaui:refresh" width="20" height="20" />
            </button>
          </div>

          {/* Toolbar */}
          <div className="view-leads-toolbar">
            <div className="view-leads-search">
              <div className="view-leads-search__wrapper">
                <div className="view-leads-search__icon-wrapper">
                  <Icon
                    icon="proicons:search"
                    width="18"
                    height="18"
                    className="view-leads-search__icon"
                  />
                </div>
                <input
                  ref={searchRef}
                  type="text"
                  className="view-leads-search__input"
                  placeholder="Search"
                  value={searchValue}
                  onChange={(e) => filteringData(e.target.value)}
                />
                {searchValue && (
                  <div className="view-leads-search__clear-wrapper">
                    <button
                      className="view-leads-search__clear"
                      onClick={() => filteringData("")}
                      aria-label="Clear search"
                      type="button"
                    >
                      <Icon 
                        icon="mdi:close" 
                        width="18" 
                        height="18" 
                        color="currentColor" 
                      />
                    </button>
                  </div>
                )}
              </div>
            </div>
            <button
              className="view-leads-download-btn"
              onClick={handleDownload}
              disabled={downloadDisabled || loading}
            >
              {loading ? (
                <Icon icon="svg-spinners:ring-resize" width="20" height="20" />
              ) : (
                <Icon icon="mdi:download" width="20" height="20" color="currentColor" style={{ display: 'block' }} />
              )}
            </button>
          </div>
        </div>

        {/* Lead List */}
        <div className="view-leads-list">
          {!filterData ? (
            <div className="view-leads-list__loading">
              <Icon icon="svg-spinners:ring-resize" width="32" height="32" />
            </div>
          ) : filterData.length === 0 ? (
            <div className="view-leads-list__empty">No leads found</div>
          ) : isDesktop ? (
            // Desktop Table View
            <table className="view-leads-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email Id</th>
                  <th>Captured At</th>
                  <th>Captured By</th>
                </tr>
              </thead>
              <tbody>
                {filterData.map((lead, index) => {
                  const isInvalid = isInvalidLead(lead);
                  return (
                    <tr key={lead.bookingId} onClick={() => handleLeadClick(lead)} className={isInvalid ? 'view-leads-table__row--invalid' : ''}>
                      <td>
                        <div className="view-leads-table__name-cell">
                          {getLeadDisplayName(lead)}
                          {isInvalid && (
                            <Icon icon="mdi:help-circle-outline" width="18" height="18" className="view-leads-table__invalid-icon" />
                          )}
                        </div>
                      </td>
                      <td>{lead.emailId || "-"}</td>
                      <td>{formatCapturedAt(lead.createdAt)}</td>
                      <td>{lead.scannedBy || "-"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            // Mobile Card View - Simple card design
            filterData.map((lead) => {
              const isUnsynced = lead.network === "false";
              const isInvalid = isInvalidLead(lead);
              const subtitle = getLeadSubtitle(lead);
              return (
                <div
                  key={lead.bookingId}
                  className={`lead-card ${isUnsynced ? 'lead-card--unsynced' : ''} ${isInvalid ? 'lead-card--invalid' : ''}`}
                  onClick={() => {
                    if (isUnsynced) {
                      setUnsyncLeadName(getLeadDisplayName(lead));
                      setUnsyncModalOpen(true);
                    } else {
                      handleLeadClick(lead);
                    }
                  }}
                >
                  <div className={`lead-card__avatar ${isInvalid ? 'lead-card__avatar--invalid' : ''}`}>
                    {isInvalid ? (
                      <Icon icon="mdi:help-circle-outline" width="24" height="24" />
                    ) : (
                      <Icon icon="mdi:account" width="24" height="24" />
                    )}
                  </div>
                  <div className="lead-card__content">
                    <h3 className={`lead-card__name${isManualLead(lead) ? ' lead-card__name--manual' : ''}`}>{getLeadDisplayName(lead)}</h3>
                    {subtitle && <p className="lead-card__subtitle">{subtitle}</p>}
                  </div>
                  {isInvalid && (
                    <div className="lead-card__invalid-icon">
                      <Icon icon="mdi:help-circle-outline" width="20" height="20" />
                    </div>
                  )}
                  {isUnsynced && !isInvalid && (
                    <div className="lead-card__unsynced-icon">
                      <CloudOffIcon width={20} height={20} />
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
        </div>
      </div>

      {/* Bottom Navbar - Always render, CSS controls visibility for desktop/iPad */}
      <BottomNavbar eventId={event_id} sponsorCode={sponsor_code} />

      {/* Outlet for modals */}
      <Outlet
        context={{
          duplicateEntry: null,
          eventId: event_id,
        }}
      />

      {/* Menu Actions Modal */}
      <MenuActionsModal
        isOpen={menuModalOpen}
        onClose={() => setMenuModalOpen(false)}
        eventId={event_id}
        sponsorCode={sponsor_code}
        onDownload={handleDownload}
        showTeam={shouldShowTeamsFeature(eventDetails?.createdAt)}
      />

      {/* Profile Modal */}
      <ProfileModal
        isOpen={profileModalOpen}
        onClose={() => setProfileModalOpen(false)}
      />

      {/* Unsync Data Modal */}
      <UnsyncDataModal
        isOpen={unsyncModalOpen}
        onClose={() => setUnsyncModalOpen(false)}
        leadName={unsyncLeadName}
      />
      <style> {
        `
        @media screen and (min-width: 992px) {
          .lead-details__close-btn{
            top:${isIPad ? '0px' : '16px'};
            right:${isIPad ? '0px' : '16px'};
          }
        }
        `}
      </style>
    </div>
    
  );
};

export default ViewLeads;
